/**
 * @file This is the starting point/file for HomePage.
 * @module Home
 * @requires "@/utils/ContentfulServices"
 * @requires "@/components/HomePageView"
 * @requires "@/components/PageMetaManager/MaticPageMeta"
 * @requires "@/components/StayUpdatedSection/StayUpdatedMain"
 */

import React, { useEffect, useState, useRef } from "react";
import HomePageView from "@/components/HomePageView";
import MaticPageMeta from "@/components/PageMetaManager/MaticPageMeta";
import StayUpdatedMain from "@/components/StayUpdatedSection/StayUpdatedMain";
import JsonLdManager from "@/components/JsonLdManager/JsonLdManager";
import { getProductPricing } from "@/utils/queries";
import { MATIC_PRODUCT_ID } from "@/consts/shopify";
import homepage from "../../cache/contentful/homepage.json";
import homePage2 from "../../cache/contentful/homePage2.json";
import pageLoadAnimation from "../../cache/contentful/pageLoadAnimation.json";
import commonAssets from "../../cache/contentful/commonAssets.json";

/**
 * Functional component for the home page.
 * @function Home
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.entries - Entries for the home page.
 * @param {Array} props.pageLoadAnimationentries - Entries for page load animation.
 * @param {Array} props.homePage2 - Entries for the second section of the home page.
 * @param {Array} props.commonAssets - Common assets for the home page.
 * @returns {JSX.Element} The rendered home page component.
 */

export default function Home({
  entries,
  pageLoadAnimationentries,
  homePage2,
  commonAssets,
  maticProduct,
}) {
  const pageMetaConfig = commonAssets[0]?.fields?.websiteMetadata || {};
  return (
    <>
      <MaticPageMeta config={pageMetaConfig} />
      <JsonLdManager
        page="home"
        breadCrumbConfig={commonAssets[0]?.fields?.breadcrumbMetaData || {}}
        homeConfig={commonAssets[0]?.fields?.homePageJsonLd || {}}
      />
      <main className="mx-auto relative">
        <HomePageView
          entries={entries}
          pageLoadAnimationentries={pageLoadAnimationentries}
          homePage2={homePage2}
          commonAssets={commonAssets}
          maticProduct={maticProduct}
        />
        <StayUpdatedMain data={commonAssets[0]?.fields} />
      </main>
    </>
  );
}

Home.getInitialProps = async () => {
  const maticProduct = await getProductPricing(MATIC_PRODUCT_ID);

  return {
    entries: homepage.items,
    pageLoadAnimationentries: pageLoadAnimation.items,
    homePage2: homePage2.items,
    commonAssets: commonAssets.items,
    maticProduct,
  };
};
